/*
  bf-training-app.js
  The BullFit Training member application.

  Structure follows the approved prototype: one application, a client router,
  a persistent shell (sidebar on desktop, scrolling tab strip on mobile) and a
  view per route. What differs is that every value here comes from the training
  backend rather than sample state — where the prototype shows 120 credits, this
  shows what the member actually has, or an empty state.

  Nothing in this file is a security boundary. The backend decides entitlement,
  what program content to release, and who may see staff views; this renders
  what it returns.
*/
(function () {
  'use strict';

  var root = document.getElementById('bf-training-app');
  if (!root) return;

  var CFG = {};
  try { CFG = JSON.parse(root.getAttribute('data-config') || '{}'); } catch (e) { CFG = {}; }

  var NAV = [
    ['home', 'Today'], ['week', 'My plan'], ['firstgym', 'Gym Mode'],
    ['videos', 'Exercise guides'], ['guides', 'My trainers'], ['progress', 'My progress'],
    ['credits', 'Credits & rewards'], ['product', 'BullFit products'],
    ['membership', 'Membership'], ['sms', 'Text check-ins']
  ];
  var ADMIN_NAV = [
    ['admin', 'Overview'], ['members', 'Members'], ['content', 'Programs & videos'],
    ['equipmentadmin', 'Equipment'], ['messaging', 'SMS operations'],
    ['fulfillment', 'Kits & rewards'], ['calls', 'Consultations'],
    ['analytics', 'Retention'], ['settings', 'Configuration'], ['flow', 'Full flow']
  ];
  var ADMIN_ROUTES = ADMIN_NAV.map(function (x) { return x[0]; });

  /* ---- state ----------------------------------------------------------
     Only what the shell needs to render. Everything member-specific is
     fetched; nothing is invented to fill a gap. */
  var S = {
    route: 'home', auth: null, me: null, program: null, progress: null,
    credits: null, sms: null, consultations: null, loading: {}, error: {}
  };

  /* ---- tiny helpers ---------------------------------------------------- */
  function esc(v) { var d = document.createElement('div'); d.textContent = v == null ? '' : String(v); return d.innerHTML; }
  function el(html) { var t = document.createElement('template'); t.innerHTML = html.trim(); return t.content.firstElementChild; }
  function money(minor, currency) {
    if (minor == null) return null;
    try { return new Intl.NumberFormat(undefined, { style: 'currency', currency: currency || 'USD' }).format(minor / 100); }
    catch (e) { return '$' + (minor / 100).toFixed(2); }
  }
  function initials(name) {
    var s = String(name || '').trim();
    if (!s) return 'BF';
    return s.split(/\s+/).slice(0, 2).map(function (w) { return w[0]; }).join('').toUpperCase();
  }

  function btn(label, route, cls, attrs) {
    return '<button class="bf-btn ' + (cls || 'ghost') + '" ' + (route ? 'data-go="' + esc(route) + '"' : '') +
      ' ' + (attrs || '') + '>' + esc(label) + '</button>';
  }
  function tag(text, colour) { return '<span class="bf-tag ' + (colour || '') + '">' + esc(text) + '</span>'; }
  /* `accent` is a phrase within the title to paint with the house gradient,
     exactly as head() does for a page title. The title is escaped first and
     the accent matched against the escaped text, so the only markup inserted
     is our own span — a panel titled from data cannot open a hole. */
  function panel(title, body, accent) {
    var t = esc(title);
    if (title && accent) {
      var a = esc(accent), i = t.indexOf(a);
      if (i > -1) t = t.slice(0, i) + '<span class="bf-accent">' + a + '</span>' + t.slice(i + a.length);
    }
    return '<section class="bf-panel">' + (title ? '<h2>' + t + '</h2>' : '') + body + '</section>';
  }
  function row(title, sub, right) {
    return '<div class="bf-row"><div><h3>' + esc(title) + '</h3>' +
      (sub ? '<p>' + esc(sub) + '</p>' : '') + '</div><div>' + (right || '') + '</div></div>';
  }
  /* `accent` is a phrase WITHIN the title to paint with the storefront's
     chrome gradient. The title is escaped first and the accent is matched
     against the escaped text, so the only markup ever inserted is our own span
     — a title containing "&" or "<" cannot open a hole here.
     Used on the member journey's own screens, never on a title that comes from
     data: an exercise name is a fact, and gradient-ing half of "Lat pulldown"
     would be decoration applied to a label. */
  function head(eyebrow, title, sub, extra, accent) {
    var t = esc(title);
    if (accent) {
      var a = esc(accent);
      var i = t.indexOf(a);
      if (i > -1) {
        t = t.slice(0, i) + '<span class="bf-accent">' + a + '</span>' + t.slice(i + a.length);
      }
    }
    return '<div class="bf-pagehead"><div>' +
      '<div class="bf-eyebrow">' + esc(eyebrow) + '</div>' +
      '<h1 class="bf-title">' + t + '</h1>' +
      (sub ? '<p class="bf-sub">' + esc(sub) + '</p>' : '') +
      '</div><div>' + (extra || '') + '</div></div>';
  }
  function note(text, warn) { return '<p class="bf-note' + (warn ? ' warn' : '') + '">' + esc(text) + '</p>'; }
  function skeleton(n) { var s = ''; for (var i = 0; i < (n || 3); i++) s += '<div class="bf-skeleton"></div>'; return s; }
  function empty(text) { return '<p class="bf-empty">' + esc(text) + '</p>'; }

  /* ---- backend --------------------------------------------------------- */
  function auth() {
    if (S.auth) return S.auth;
    if (!window.BFTrainingAuth || !window.BF_TRAINING_AUTH_CONFIG) return null;
    S.auth = new window.BFTrainingAuth(window.BF_TRAINING_AUTH_CONFIG);
    return S.auth;
  }
  function signedIn() { var a = auth(); return !!(a && a.isSignedIn && a.isSignedIn()); }
  async function api(path, opts) {
    var a = auth();
    if (!a) return { ok: false, status: 0, reason: 'not_ready' };
    return a.api(path, opts);
  }

  /* THE TWO ROUTES A VISITOR WITH NO ACCOUNT MAY REACH.
   *
   * Onboarding is public — the questions are about the person asking and the
   * reveal is the offer — so the plan preview and the option list have to be
   * fetchable before anyone signs in. api() cannot do it: it needs an access
   * token and returns 401 without ever reaching the network, which is why the
   * reveal had nothing to name for a signed-out visitor no matter what the
   * backend would have said.
   *
   * So this sends the request with NO Authorization header at all. It is not
   * an unauthenticated back door: both routes still sit behind the app-proxy
   * signature, which Shopify adds on the way through, and both are written to
   * return metadata only. The allow-list is here rather than at the call site
   * so a future screen cannot quietly fetch a member route this way. */
  var PUBLIC_PATHS = ['/plan-preview', '/plan-options'];
  async function pub(path) {
    var base = (window.BF_TRAINING_AUTH_CONFIG || {}).apiBase;
    if (!base) return { ok: false, status: 0, data: null, reason: 'not_connected' };
    var root = String(path).split('?')[0];
    if (PUBLIC_PATHS.indexOf(root) === -1) {
      /* A programming error, not a member-facing state. Fail closed. */
      return { ok: false, status: 0, data: null, reason: 'not_public' };
    }
    var res;
    try {
      res = await fetch(base.replace(/\/$/, '') + path, {
        method: 'GET', headers: { 'Accept': 'application/json' },
        credentials: 'same-origin'
      });
    } catch (e) { return { ok: false, status: 0, data: null, reason: 'network' }; }
    var data = null;
    try { data = await res.json(); } catch (e) {}
    return { ok: res.ok, status: res.status, data: data,
             reason: res.ok ? null : (data && data.error) || ('http_' + res.status) };
  }

  /** Loads a resource once, tracks its loading and error state honestly. */
  async function load(key, path, force) {
    if (S[key] && !force) return S[key];
    S.loading[key] = true;
    var r = await api(path);
    S.loading[key] = false;
    if (!r.ok && r.status !== 403 && r.status !== 200) { S.error[key] = r.reason || ('http_' + r.status); }
    S[key] = r.data || null;
    S[key + 'Status'] = r.status;
    return S[key];
  }

  /* ---- shell ----------------------------------------------------------- */
  function isStaffRoute(r) { return ADMIN_ROUTES.indexOf(r) > -1; }

  function topBar() {
    var name = (S.me && S.me.firstName) || (CFG.customerName || '');
    var access = S.me && S.me.access;
    /* A preview account must be labelled on EVERY screen.
     *
     * This read `S.progress.preview`, which is a different endpoint's payload:
     * on any route that does not load /progress it was undefined, so a comped
     * account fell through and was badged "Member". Staff screens already take
     * care never to read preview usage as revenue, and the member-facing badge
     * has to be just as honest.
     *
     * `/me` carries the authoritative answer on every route — the entitlement
     * reason is `comped` for exactly these accounts — so that is what decides
     * it, with the progress flag kept as a fallback. */
    var preview = (access && access.reason === 'comped') ||
      !!(S.progress && S.progress.preview);
    var badge = !signedIn() ? ''
      : preview ? '<span class="bf-badge is-preview">Preview</span>'
      : (access && access.hasAccess) ? '<span class="bf-badge">Member</span>'
      : '<span class="bf-badge" style="border-color:var(--bf-line);color:var(--bf-muted)">Visitor</span>';
    return '<header class="bf-top">' +
      '<div class="bf-brand">' +
        (CFG.logoUrl ? '<img class="bf-logo" src="' + esc(CFG.logoUrl) + '" alt="BullFit" style="height:30px">' :
          '<strong style="font-family:var(--bf-display,Anton);font-size:22px;letter-spacing:.04em">BULLFIT</strong>') +
        '<span class="bf-wordmark">Training</span>' +
      '</div>' +
      '<nav class="bf-topnav" aria-label="Training navigation">' +
        '<button class="bf-link" data-go="week">The program</button>' +
        '<button class="bf-link" data-go="credits">Earn credits</button>' +
        '<button class="bf-link" data-go="guides">Your trainers</button>' +
        '<div class="bf-identity">' + badge +
          '<span class="bf-avatar" aria-hidden="true">' + esc(initials(name || 'BullFit')) + '</span>' +
          /* A WAY OUT. auth.signOut() has existed since the beginning and the
             only button wired to it lives in bf-training-member.liquid, a
             different section — so inside this app there was no way to sign
             out at all. On a shared phone that means the next person opens
             somebody else's training; for staff it means no way to swap from a
             preview account to a named one. Rendered only when there is a
             session to end. */
          (signedIn()
            ? '<button type="button" class="bf-signout" data-signout>Sign out</button>'
            : '') +
        '</div>' +
      '</nav></header>';
  }

  /* A compact brand header, a way out, and the question. Nothing else.

     THE WAY OUT MATTERS. This screen replaces the whole page — no storefront
     header, no nav — so without an explicit link the only exit is the browser
     back button. Someone who opened the questionnaire to look at it was
     stranded. The answers already survive leaving, so the link costs nothing
     and the alternative is a visitor who feels trapped by a form. */
  function bareShell(body) {
    var landing = CFG.landingUrl || '/pages/bullfit-training';
    return '<div class="bf-onb-frame"><header class="bf-onb-head">' +
      /* The full label is the accessible name at every width. The tail is
         hidden on a narrow screen, where "Back to BullFit Training" wrapped
         onto three lines and took over the header. */
      '<a class="bf-onb-exit" href="' + esc(landing) + '" aria-label="Back to BullFit Training">' +
        '<span aria-hidden="true">\u2190</span> Back' +
        '<span class="bf-onb-exit-rest" aria-hidden="true"> to BullFit Training</span></a>' +
      '<span class="bf-onb-brand">BULLFIT <b>TRAINING</b></span>' +
      '<span class="bf-onb-tag">A plan that starts with you.</span>' +
      '</header><main class="bf-onb-main">' + body + '</main></div>';
  }

  function shell(body) {
    var admin = isStaffRoute(S.route);
    var list = admin ? ADMIN_NAV : NAV;
    var items = list.map(function (x, i) {
      return '<button class="bf-navitem" data-go="' + x[0] + '"' +
        (S.route === x[0] ? ' aria-current="page"' : '') + '>' +
        '<span class="bf-navnum">' + String(i + 1).padStart(2, '0') + '</span>' + esc(x[1]) + '</button>';
    }).join('');
    var help = '<div class="bf-sidehelp"><div class="bf-marker">' +
      (admin ? 'Keep it running.' : 'Keep bullish.') + '</div><p>' +
      esc(admin ? 'Operational records. Member data is real.' :
                  'A small step counts. Your progress stays with you.') + '</p>' +
      btn(admin ? 'Member experience' : 'Get some help', admin ? 'home' : 'guides', 'ghost small') + '</div>';
    return topBar() + '<div class="bf-app"><aside class="bf-sidebar">' +
      '<div class="bf-navlabel">' + (admin ? 'BullFit operations' : 'Your training club') + '</div>' +
      items + help + '</aside><main class="bf-main">' + body + '</main></div>';
  }

  /* ---- gates ----------------------------------------------------------- */
  /* Recorded in ONE place because there are TWO ways to reach '#save' — this
     file's [data-go] navigation and the reveal's own [data-onb-signin]
     handler in bf-training-actions.js. The first version set the flag here
     only, so the button members actually press did not set it and the save
     refused itself. Survives the sign-in round trip: same tab, same origin. */
  function markSaveIntent() {
    try { sessionStorage.setItem('bf_save_intent', String(Date.now())); } catch (e) {}
  }

  /* THE SAME FRAME AS THE QUESTIONNAIRE GATE, because this is the same
     moment: someone is here without an account. There are two ways to arrive
     signed out — the app's own URL and the landing call to action — and they
     used to disagree about what that looks like. This one wrapped its message
     in shell(), the member dashboard: a brand header with an avatar, and the
     member nav. So a visitor got three stacked headers and a row of tabs where
     every item is a gated member route, which meant pressing "Workout videos"
     re-rendered this very message. A control that looks like a choice and is
     not one is worse than no control.

     bareShell already carries the brand and the way back to the storefront, so
     the gate is the sentence and the button and nothing else. */
  function signInGate() {
    return bareShell(
      '<section class="bf-onb-q">' +
      '<div class="bf-eyebrow">BullFit Training</div>' +
      '<h1 class="bf-onb-h1" id="bf-q-title" tabindex="-1">' +
        'PICK UP<br><span class="bf-accent">WHERE YOU LEFT OFF.</span></h1>' +
      '<p class="bf-onb-intro">Sign in to reach your plan. Your logged sets ' +
      'and your progress are tied to your BullFit account.</p>' +
      '<div class="bf-onb-actions">' +
      '<button type="button" class="bf-onb-next" data-signin>' +
      'Sign in <span aria-hidden="true">\u2192</span></button></div>' +
      '</section>');
  }

  /* The member gate says "pick up where you left off", which is the right
     sentence for someone returning to their plan and the wrong one for someone
     who has just clicked "Tell us how you train" and never answered anything.
     Onboarding gets its own, in the bare shell, so arriving at question one
     does not dump a dashboard sidebar on a visitor who has no account yet. */
  function onboardingGate() {
    return bareShell(
      '<section class="bf-onb-q">' +
      '<div class="bf-eyebrow">First, your account</div>' +
      '<h1 class="bf-onb-h1" id="bf-q-title" tabindex="-1">' +
        'SIGN IN<br><span class="bf-accent">TO BEGIN.</span></h1>' +
      /* WHAT THIS DOES AND DOES NOT PROMISE. Answers are still written at the
   end, by persist(), not after each question — so this must not say they
   save as you type, and must not promise another device. What signing in
   first genuinely buys is that there is an account waiting when the save
   happens, instead of a sign-in that can fail after six questions. */
      '<p class="bf-onb-intro">Signing in first means your answers have an ' +
      'account to go to. You will not reach the end and find they could not ' +
      'be saved.</p>' +
      '<div class="bf-onb-actions">' +
      '<button type="button" class="bf-onb-next" data-signin>' +
      'Sign in and start <span aria-hidden="true">\u2192</span></button></div>' +
      '<p class="bf-onb-help">Creating an account is free. There is no payment ' +
      'anywhere in this questionnaire.</p>' +
      '</section>');
  }

  function staffGate() {
    return shell(head('BULLFIT OPERATIONS', 'This account does not have access.',
      'Staff views are limited to named accounts.'));
  }

  window.__bfRender = render;
  window.__bfMarkSaveIntent = markSaveIntent;

  /* ---- views ----------------------------------------------------------- */
  var views = {};

  /* Registered by bf-training-screens.js so this file stays readable. */
  window.BFTrainingApp = {
    register: function (name, fn) { views[name] = fn; },
    helpers: { esc: esc, el: el, money: money, btn: btn, tag: tag, panel: panel, row: row,
               head: head, note: note, skeleton: skeleton, empty: empty, api: api, pub: pub, load: load,
               state: S, config: CFG, signedIn: signedIn, routeParam: routeParam,
               /* FORWARDS ITS ARGUMENT AND RETURNS THE PROMISE. It did neither:
                  `render()` is async, so `await H.render()` was awaiting
                  `undefined` and resolving the moment render() had replaced the
                  screen with its loading skeleton. Everything the save path did
                  afterwards — putting the member's typed numbers back — ran
                  against that skeleton and was thrown away by the real render a
                  moment later. It had never worked. */
               render: function (opts) { return render(opts); } },
    navs: { member: NAV, admin: ADMIN_NAV }
  };

  /* Answerable without an account: the setup questions and the plan preview. */
  /* ONBOARDING RUNS WITHOUT THE MEMBER SHELL.
     One question at a time means no side navigation, no tab strip and no
     marketing footer competing with it — just the brand header and the
     question. The dashboard shell is untouched for every other route.

     SEPARATE from PUBLIC_ROUTES below, and it has to be. These were the same
     array, so closing the questionnaire to signed-out visitors would ALSO have
     stripped the bare shell off it and rendered the six questions inside the
     member dashboard, sidebar and all. Two different questions — which screens
     are plain, and which screens need an account — now have two answers. */
  var BARE_ROUTES = ['resume', 'routine', 'goal', 'comfort', 'equipment',
                     'days', 'time', 'plan', 'save'];

  /* NOTHING IS PUBLIC.
     The questionnaire used to run signed out: answers were held in this
     browser and written to the account on the first authenticated save. That
     handed every visitor a trip they could fail — the save at the end could be
     refused, and until it succeeded the answers lived only on one device.

     Sign-in now comes first, so an answer belongs to an account from the
     moment it is given. The cost is real and deliberate: someone must have an
     account before they can answer a question about themselves, and some
     people will leave at that door. The gate says so plainly rather than
     dressing it up as a formality. */
  var PUBLIC_ROUTES = [];

  /* ---- routing --------------------------------------------------------- */
  /* A route may carry ONE parameter after a slash — `#player/s2` opens the
     video for that exercise. Split here so every screen reads it the same way
     and no screen has to parse the hash itself. */
  var routeArg = null;
  function routeFromHash() {
    var h = (location.hash || '').replace(/^#/, '').split('?')[0];
    var slash = h.indexOf('/');
    if (slash > -1) {
      routeArg = decodeURIComponent(h.slice(slash + 1));
      return h.slice(0, slash) || 'home';
    }
    routeArg = null;
    return h || 'home';
  }
  function routeParam() { return routeArg; }

  /* `opts.keepPosition` means REFRESH, not NAVIGATE.
     The only difference is the scroll at the end, and conflating the two cost
     a tester her place on every set she logged: saving re-draws the workout to
     recompute the counters and the circuit's next step, and the re-draw was
     reusing the jump-to-top that belongs to moving between screens. She had to
     scroll back down after every save. */
  /* WHICH RENDER OWNS THE SCREEN.
     Every render takes a ticket. Only the newest one may paint, because the
     response for a screen the member has already left can land after the one
     they are looking at — and it used to win, simply by arriving second. From
     My Plan, opening a question, pressing Back and then Today left "Your week,
     made clear" on a page whose URL said #home, and no amount of clicking
     recovered it. */
  var renderSeq = 0;

  async function render(opts) {
    var r = S.route;
    if (!views[r]) { S.route = 'home'; r = 'home'; }

    /* ONE QUESTION, NOTHING COMPETING WITH IT.
       The design asks for a compact brand header and the question alone — no
       shop navigation, no marketing footer, no announcement bar. Those belong
       to the storefront layout rather than to this section, so the class goes
       on the document and the CSS hides them.
       Set HERE, before any gate can return early: a signed-out visitor landing
       on a private route still has to get the storefront back.

       Keyed to THE FRAME, not to the route list. While only the questionnaire
       used the bare frame those were the same set; the sign-in gate now uses
       it on any route, and a signed-out visitor is always shown one gate or
       the other. Keyed to the route alone, the gate rendered a frame asking
       for the full viewport underneath the storefront header that was still
       there — 1887px of document at 375x812 for a single button, the heading
       sitting past the half-way line behind an empty band. */
    var bare = BARE_ROUTES.indexOf(r) > -1 || !signedIn();
    try {
      document.documentElement.classList.toggle('bf-onboarding-focus', bare);
    } catch (e) {}

    /* THE GATE, AND WHICH GATE.
     *
     * This comment used to say the opposite of the code. It described a
     * questionnaire that ran signed out, with answers "held locally while
     * signed out and written to the account on the first authenticated save".
     * That was true once and was deliberately reverted — see NOTHING IS
     * PUBLIC above PUBLIC_ROUTES, which is now empty, so EVERY route is
     * gated. A stale comment is worse here than no comment: this is the
     * single most consequential decision in the funnel, and the file
     * disagreed with itself about what the decision was.
     *
     * What is still true is the part that fixed a real complaint: WHICH gate
     * a visitor meets. A first-time visitor on an onboarding route is asked
     * to make an account before answering questions about themselves, and
     * told why; a returning member on any other route is offered their plan
     * back. Those are different people and they get different words. */
    if (!signedIn() && PUBLIC_ROUTES.indexOf(r) === -1) {
      root.innerHTML = BARE_ROUTES.indexOf(r) > -1 ? onboardingGate() : signInGate();
      return;
    }
    if (isStaffRoute(r) && S.me && S.me.staff !== true) { root.innerHTML = staffGate(); return; }

    // Paint the shell immediately with a loading body, then fill it. A screen
    // that appears instantly and then resolves reads better than a blank wait.
    var frame = bare ? bareShell : shell;
    var seq = ++renderSeq;
    /* A REFRESH DOES NOT BLANK THE SCREEN.
       Replacing the body with a skeleton collapses the page to a few hundred
       pixels, and the browser clamps the scroll position to the top of what is
       left. Keeping the position afterwards then LOOKS like a bug with a delay
       in it: reported from an iPhone as "it takes me to the top, sits there
       for a second or two, and then jumps back to the proper spot" — the two
       seconds being the request, and the jump being the restore arriving after
       it. Navigation still shows the skeleton, because there the old screen is
       genuinely gone. */
    if (!(opts && opts.keepPosition)) {
      root.innerHTML = frame('<div class="bf-loading bf-panel">' + skeleton(4) + '</div>');
    }
    var body;
    try { body = await views[r](); }
    catch (e) {
      body = head('SOMETHING WENT WRONG', 'That screen did not load.',
        'The rest of the app still works.') + panel('', note('Try again in a moment.', true));
    }
    /* Someone else asked after we did, so the page is theirs now. Painting
       here would replace the screen the member is actually on. */
    if (seq !== renderSeq) return;
    root.innerHTML = frame(body);
    var main = root.querySelector('.bf-main');
    if (main && !(opts && opts.keepPosition)) main.scrollIntoView({ block: 'start' });
  }

  root.addEventListener('click', function (e) {
    var go = e.target.closest('[data-go]');
    if (go) {
      /* A SAVE HAPPENS BECAUSE SOMEONE PRESSED SAVE.
         '#save' writes to the account on arrival, so anything that merely
         LANDS there writes too — including the return leg of a sign-in. A
         member gated at an old '#save' link would come back and have whatever
         this browser happened to hold posted to their account without ever
         choosing to. The flag is set only by a press, consumed by the save,
         and its absence means the arrival was not a decision. */
      if (go.getAttribute('data-go') === 'save') markSaveIntent();
      var to = go.getAttribute('data-go');
      /* THE SAME DESTINATION STILL HAS TO WORK. Assigning the hash it already
         holds fires no hashchange, so tapping the nav item for the screen you
         are on did nothing — which is exactly the tap someone makes when the
         screen looks wrong. Render it directly. */
      if (routeFromHash() === to) { S.route = to; render(); return; }
      location.hash = '#' + to; return;
    }
    var href = e.target.closest('[data-href]');
    if (href) { location.href = href.getAttribute('data-href'); return; }
    var si = e.target.closest('[data-signin]');
    if (si) { var a = auth(); if (a) a.signIn(location.href); return; }
    /* signOut() clears the local tokens BEFORE redirecting, so a failed or
       blocked redirect still leaves this browser signed out rather than
       looking signed in with a session the member thinks they ended. */
    var so = e.target.closest('[data-signout]');
    if (so) { var ao = auth(); if (ao) ao.signOut(); return; }
    /* Registered before boot runs, so it still works on the screen that says
     * boot failed — which is the one place it matters most. */
    if (e.target.closest('[data-reload]')) { location.reload(); return; }
  });

  window.addEventListener('hashchange', function () {
    S.route = routeFromHash();
    render();
  });

  (function start(n) {
    if (window.BFTrainingAuth && window.BF_TRAINING_AUTH_CONFIG && window.__bfScreensReady) {
      S.route = routeFromHash();
      if (signedIn()) {
        /* RENDER EVEN IF THE LOAD DID NOT.
           Nothing past this point may depend on /me having answered. Every
           screen is already written for a member whose record has not arrived,
           and render() reads S.me defensively — but a REJECTION skipped
           render() altogether, leaving the server's boot skeleton on screen
           with nothing to read and nothing to press. That is strictly worse
           than any screen the app could have drawn, so the only promise at
           boot ends in a paint no matter which way it settles. */
        load('me', '/me').catch(function () {}).then(function () { render(); });
      } else { render(); }
      return;
    }
    /* Boot gave up.
     *
     * The old message was a full stop: one sentence, no way forward, on a page
     * the visitor reached by being told to tell us how they train. Reloading
     * genuinely fixes most of these — a script that lost the network is the
     * common case — so offer it, and offer the way back for when it does not.
     *
     * What is NOT claimed here: that their answers are safe. Boot failed before
     * anything could be read, so there is nothing to reassure them about and
     * saying otherwise would be a guess. */
    if (n > 60) {
      /* Built WITHOUT esc() or btn(). Both are fine normally, but esc()
       * escapes by creating a detached node, so the one screen whose entire
       * job is to appear when the app is broken would depend on the DOM API
       * being healthy. If that threw, the visitor would keep the loading
       * skeleton forever — precisely the failure this screen exists to end.
       * So the failure path carries its own string escaping and reaches for
       * nothing it does not already have. */
      var safe = String(CFG.landingUrl || '/pages/bullfit-training')
        .replace(/&/g, '&amp;').replace(/</g, '&lt;')
        .replace(/>/g, '&gt;').replace(/"/g, '&quot;');
      root.innerHTML =
        '<div class="bf-boot"><header class="bf-boot-head">' +
        '<span class="bf-boot-brand">BULLFIT <b>TRAINING</b></span></header>' +
        '<div class="bf-boot-body bf-boot-failed" role="alert">' +
        '<h2>THIS SCREEN DID NOT LOAD</h2>' +
        '<p>Part of the app did not arrive. This is usually the connection, not your account.</p>' +
        '<div class="bf-boot-actions">' +
        '<button class="bf-btn primary" data-reload>Reload</button>' +
        '<button class="bf-btn ghost" data-href="' + safe + '">Back to BullFit Training</button>' +
        '</div></div></div>';
      return;
    }
    setTimeout(function () { start(n + 1); }, 120);
  })(0);
})();
